import { deserializeAs } from 'cerialize';

import { DonationPartnerState } from './donation-partner-state';

export enum PartnerAssignmentType {
  auto = 'auto',
  manual = 'manual',
  last_call = 'last_call',
}

export const PARTNER_ASSIGNMENT_TYPE_LABELS: { [key in PartnerAssignmentType]: string } = {
  [PartnerAssignmentType.auto]: 'Auto',
  [PartnerAssignmentType.manual]: 'Manual',
  [PartnerAssignmentType.last_call]: 'Last Call',
};

// `close_event` IS the state-machine event that took the partner off the donation. The API keeps no
// allow-list for it, so this is an OPEN set: label the values that actually occur and let anything else
// fall through to its raw value, which is what you want when debugging.
//
// `partner_cancel` is labelled just "Canceled" on purpose. The event name is the *partner state
// machine's* cancel, not "the partner cancelled" — over 90 days it was fired 5,386 times and 100% of
// those were captains (it is the cancel button in ops), while `partner_decline` was 100% partner/driver
// and never a captain. Naming an actor here would be guesswork, so the label states only what happened.
//
// Deliberately NOT labelled: `missing`, the API's sentinel for "partner released without a transition
// doing it" (a plain PUT rather than a state action). Across 90 days of production, every single
// partner_id change came from a real state event — `donation_update` never changed a partner once — so
// this cannot currently happen, and if it ever does the raw `missing` showing through is more useful
// than a friendly label that hides how it got there.
export const PARTNER_ASSIGNMENT_CLOSE_EVENT_LABELS: Record<string, string> = {
  auto_decline: 'Timed Out',
  partner_decline: 'Declined by Partner',
  partner_request_reschedule: 'Reschedule Requested',
  partner_cancel: 'Canceled',
  auto_cancel: 'Auto Canceled',
};

export function partnerAssignmentCloseEventLabel(closeEvent?: string | null): string | null {
  if (!closeEvent) {
    return null;
  }
  return PARTNER_ASSIGNMENT_CLOSE_EVENT_LABELS[closeEvent] ?? closeEvent.replace(/_/g, ' ');
}

// Deserialize-only on purpose (RP-3920): the assignment audit trail must never be sent back on donation
// updates — captains can mass-assign donation data attributes, so a serialized echo would silently
// rewrite the history.
//
// There is deliberately no timestamp:
// the ARRAY ORDER is the chronology — `partner_assignments` is oldest first, and there is no singular
// slot: the offer still in play is simply the newest entry with no close_event.
//
// `vehicle_id` is the truck the job was OFFERED with. A vehicle swap does not open a new attempt, so
// after one this can differ from `donation.vehicleId` — the record is the offer, the donation is the
// present truth.
export class PartnerAssignment implements IPartnerAssignment {
  @deserializeAs('type') type?: PartnerAssignmentType;
  @deserializeAs('round') round?: number | null;
  @deserializeAs('partner_id') partnerId?: string | null;
  @deserializeAs('vehicle_id') vehicleId?: string | null;
  @deserializeAs('user_id') userId?: string | null;
  @deserializeAs('close_event') closeEvent?: string | null;

  constructor(payload?: IPartnerAssignment) {
    this.type = payload?.type;
    this.round = payload?.round ?? null;
    this.partnerId = payload?.partnerId?.toString() || null;
    this.vehicleId = payload?.vehicleId?.toString() || null;
    this.userId = payload?.userId?.toString() || null;
    this.closeEvent = payload?.closeEvent ?? null;
  }

  // Mirrors the API's `ongoing?` — purely "nothing has closed this". Says nothing about whether the
  // offer is still open in a business sense; use it only to find position in the trail.
  get closed(): boolean {
    return !!this.closeEvent;
  }

  // Whether the offer is genuinely still in the air. An unclosed attempt is NOT automatically open: once
  // the partner accepts, the offer is resolved and the job is being worked, even though the API leaves
  // close_event null all the way through completion. Only `assigned` means nobody has answered yet.
  ongoing(partnerState?: DonationPartnerState): boolean {
    return !this.closed && partnerState === DonationPartnerState.assigned;
  }
}

export interface IPartnerAssignment {
  type?: PartnerAssignmentType;
  round?: number | null;
  partnerId?: string | null;
  vehicleId?: string | null;
  userId?: string | null;
  closeEvent?: string | null;
}
